'use client';

import { Form } from 'informed';
import { useRouter, useSearchParams } from 'next/navigation';
import { type FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useSWRMutation from 'swr/mutation';

import {
  type RegisterForm,
  register,
  UpdatePlayerContext,
  type User,
  updatePlayer,
} from '@/api/auth';
import { Button, ButtonClose } from '@/components/buttons';
import { RegistrationBanner } from '@/components/dialogs/Registration/components/RegistrationBanner';
import { FormGroup } from '@/components/form/groups/FormGroup';
import { Password } from '@/components/form/Password';
import { Modal } from '@/components/Modal';
import {
  AUTO_REGISTRATION_QUERY_NAME,
  LOCAL_STORAGE_REG_FORM_KEY,
  LOCAL_STORAGE_USER_INFO_FORM_KEY,
} from '@/helpers/auth';
import { removeQueryParameter } from '@/helpers/common';
import { X_SELL_USER_API } from '@/helpers/constants';
import { showTooltip } from '@/helpers/tooltip';
import { obscureEmail } from '@/helpers/userData';
import { emailRe, validatePassword } from '@/helpers/validators';
import { adaptMutation } from '@/modules/common/helpers/swr';
import { useGetCommonStore } from '@/store';
import { REASON_FOR_UPDATING_USER } from '@/store/commonStore/slices/auth/helpers';
import { TooltipTypes } from '@/store/tooltipStore';

import { ScAutoRegistration } from './styled';

export const AutoRegistrationModal: FC = () => {
  const { push } = useRouter();

  const searchParams = useSearchParams();
  const queryAutoReg = searchParams.get(AUTO_REGISTRATION_QUERY_NAME);
  const useCommonStore = useGetCommonStore();

  const isAuthenticated = useCommonStore.use.isAuthenticated();
  const pendingUser = useCommonStore.use.pendingUser();

  const [userData, setUserData] = useState();

  useEffect(() => {
    if (pendingUser) return;

    if (queryAutoReg) {
      if (isAuthenticated) {
        removeQueryParameter(AUTO_REGISTRATION_QUERY_NAME, push);

        return;
      }

      const getUserByHash = async (queryAutoReg: string) => {
        try {
          const user = await fetch(`${X_SELL_USER_API}/${queryAutoReg}`);
          const userData = await user?.json();

          setUserData(userData);
        } catch (_) {
          removeQueryParameter(AUTO_REGISTRATION_QUERY_NAME, push);
          showTooltip({
            type: TooltipTypes.ERROR,
            body: 'Hash is invalid',
          });
        }
      };

      getUserByHash(queryAutoReg);
    }
  }, [isAuthenticated, pendingUser, queryAutoReg, push]);

  return queryAutoReg && userData ? (
    <AutoRegistration userData={userData} />
  ) : null;
};

type AutoRegistrationFormFields = Pick<RegisterForm, 'password'>;

interface AutoRegistrationProps {
  userData: Partial<User> | null;
}

const AutoRegistration: FC<AutoRegistrationProps> = ({ userData }) => {
  const { t } = useTranslation('common');
  const { push } = useRouter();
  const useCommonStore = useGetCommonStore();

  const fetchUser = useCommonStore.use.fetchUser();

  const {
    trigger: requestRegister,
    error: registerError,
    isMutating: registerPending,
  } = useSWRMutation('registerKey', adaptMutation(register), {
    onError: () => {
      showTooltip({
        type: TooltipTypes.ERROR,
        body: t('errorUnknown'),
      });
    },
  });

  const {
    trigger: requestUpdatePlayer,
    error: updatePlayerErrors,
    isMutating: pendingUpdatePlayer,
  } = useSWRMutation('updatePlayer', adaptMutation(updatePlayer), {
    onError: () => {
      showTooltip({
        type: TooltipTypes.ERROR,
        body: t('errorUnknown'),
      });
    },
  });

  const handleSubmit = useCallback(
    async ({ password }: AutoRegistrationFormFields) => {
      if (!(userData?.email && userData.currency)) return;

      if (!emailRe.test(userData.email)) {
        showTooltip({
          type: TooltipTypes.ERROR,
          body: 'Email is wrong',
        });

        return;
      }

      const { email, currency, ...additionalInfo } = userData;
      const withStag = !!localStorage.getItem('isStag');

      const newUser = await requestRegister({
        email,
        currency,
        password,
        receivePromos: withStag,
        receiveSmsPromos: withStag,
        receivePromosViaPhoneCalls: withStag,
        agreedToPartnerPromotions: true,
        ageAcceptance: true,
        termsAcceptance: true,
      });

      if (newUser?.id) {
        await requestUpdatePlayer({
          context: UpdatePlayerContext.Edition,
          player: additionalInfo,
        });
      }

      await fetchUser(REASON_FOR_UPDATING_USER.REGISTRATION);

      removeQueryParameter(AUTO_REGISTRATION_QUERY_NAME, push);
    },
    [fetchUser, requestRegister, requestUpdatePlayer, userData, push]
  );

  const handleClose = useCallback(() => {
    if (userData) {
      const { email, currency, ...additionalInfo } = userData;

      localStorage.setItem(
        LOCAL_STORAGE_REG_FORM_KEY,
        JSON.stringify({ email, currency })
      );
      localStorage.setItem(
        LOCAL_STORAGE_USER_INFO_FORM_KEY,
        JSON.stringify(additionalInfo)
      );
    }
    removeQueryParameter(AUTO_REGISTRATION_QUERY_NAME, push);
  }, [userData, push]);

  const error = useMemo(() => {
    if (registerError == null) return null;

    if (registerError.email != null && registerError.email.taken != null) {
      return registerError.email.taken;
    }

    if (registerError != null || updatePlayerErrors != null) {
      const firstError = Object.keys([
        ...registerError,
        ...updatePlayerErrors,
      ]).flatMap((key) => Object.values(registerError[key]));

      return firstError;
    }
  }, [registerError, updatePlayerErrors]);

  return (
    <Modal>
      <ScAutoRegistration>
        <ButtonClose gray onClick={handleClose} right={12} top={12} />
        <h2>{t('auth.registration.title')}</h2>

        <RegistrationBanner />

        <Form<AutoRegistrationFormFields>
          onSubmit={({ values }) => handleSubmit(values)}
        >
          <p className="description">{t('auth.autoRegText')}</p>
          <p className="user-email">{obscureEmail(userData?.email || '')}</p>

          <FormGroup error={error} title={t('auth.registration.form.password')}>
            <Password
              name="password"
              strengthChecker
              validate={validatePassword}
              validateOn="change"
            />
          </FormGroup>

          <Button
            isLoading={registerPending || pendingUpdatePlayer}
            large
            primary
            type="submit"
            wide
          >
            {t('button.register')}
          </Button>
        </Form>
      </ScAutoRegistration>
    </Modal>
  );
};
